/** Centimes, dates, heure, libellé d’exercice. */
import { DEFAULT_FORMATS, type CompanyFormats } from "./types";

export const MONTHS_FR = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

/** 1.000 → 1 · 1.5 → 1,5 (si virgule). */
export function formatQty(raw: string | number | null | undefined, decimalComma = true): string {
  const n = Number(String(raw ?? "").trim().replace(/\s/g, "").replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return "1";
  const s = n.toFixed(3).replace(/\.?0+$/, "");
  return decimalComma ? s.replace(".", ",") : s;
}

export function formatCents(cents: number, f: CompanyFormats = DEFAULT_FORMATS): string {
  const neg = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const euros = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, "0");
  const dec = f.decimal_comma ? "," : ".";
  const thou = f.decimal_comma ? "\u00a0" : ",";
  const int = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, thou);
  const num = `${neg ? "−" : ""}${int}${dec}${frac}`;
  const cur = f.currency_display === "code" ? "EUR" : "€";
  return `${num}\u00a0${cur}`;
}

export function formatDay(iso: string | null | undefined, _f: CompanyFormats = DEFAULT_FORMATS): string {
  if (!iso) return "—";
  const d = iso.slice(0, 10);
  const [y, m, day] = d.split("-");
  if (!y || !m || !day) return iso;
  return `${day}/${m}/${y}`;
}

/** Date + heure brute (sans conversion de fuseau). */
export function formatWhen(iso: string | null | undefined, f: CompanyFormats = DEFAULT_FORMATS): string {
  const date = formatDay(iso, f);
  if (!iso || date === "—") return date;
  const t = iso.match(/(?:T| )(\d{2}:\d{2})/)?.[1];
  return t ? `${date} · ${t}` : date;
}

function zoneOffsetMs(instant: Date, timeZone: string): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(instant)) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  const asUtc = Date.UTC(+map.year, +map.month - 1, +map.day, +map.hour, +map.minute, +map.second);
  return asUtc - instant.getTime();
}

function parseZoned(iso: string, timeZone: string): Date | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || "0"));
  let instant = guess - zoneOffsetMs(new Date(guess), timeZone);
  instant = guess - zoneOffsetMs(new Date(instant), timeZone);
  return new Date(instant);
}

function formatInZone(date: Date, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat("fr-FR", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  return `${map.day}/${map.month}/${map.year} · ${map.hour}:${map.minute}`;
}

/** Locale + serveur. `storedTz` = fuseau dans lequel la valeur a été écrite. */
export function formatClock(
  iso: string | null | undefined,
  storedTz = "UTC"
): { primary: string; extra: string } {
  if (!iso) return { primary: "—", extra: "" };
  const date = parseZoned(iso, storedTz);
  if (!date) return { primary: formatWhen(iso), extra: "" };
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const local = formatInZone(date, localTz);
  const server = formatInZone(date, storedTz);
  const label = storedTz === "UTC" ? "UTC" : storedTz.replace(/_/g, " ");
  if (local === server) {
    return { primary: local, extra: `Heure serveur ${label}` };
  }
  return { primary: local, extra: `Heure locale · serveur ${server} ${label}` };
}

export function formatTimePreview(f: CompanyFormats): string {
  return f.time_24h ? "14:30" : "2:30 PM";
}

export function fiscalYearLabel(f: CompanyFormats): string {
  const start = MONTHS_FR[(f.fiscal_year_start - 1 + 12) % 12];
  const end = MONTHS_FR[(f.fiscal_year_start + 10) % 12];
  return `${start} → ${end}`;
}
